import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {
  type FilterDefinition,
  FiltersProvider,
  useFilters,
} from '@spotify/confidence-core-react';
import { AdminLabelsV1LabelResourceType } from '@spotify/confidence-graphql';

import {
  type LabelDefinition,
  type LabelEntry,
  useLabelsForResource,
} from '../../../hooks';
import {
  LABEL_FILTER_ID,
  createGroupedLabelFilter,
  formatLabelValue,
} from './LabelFilter';

type LabelFiltersContextValue = {
  labelDefinitions: LabelDefinition[];
  labelDefinitionsById: ReadonlyMap<string, LabelDefinition>;
  toggleLabelFilter: (labelKey: string, value: string) => void;
};

export type DefinedLabel = {
  key: string;
  value: string;
  definition: LabelDefinition;
};

export type LabelFilters = {
  labelDefinitions: LabelDefinition[];
  definedLabels: DefinedLabel[];
  hasLabels: boolean;
  toggleLabelFilter: (labelKey: string, value: string) => void;
};

const LabelFiltersContext = createContext<LabelFiltersContextValue>({
  labelDefinitions: [],
  labelDefinitionsById: new Map(),
  toggleLabelFilter: () => {},
});

const EMPTY_LABELS: LabelEntry[] = [];

type FiltersProviderWithLabelsProps = {
  filters?: Parameters<typeof FiltersProvider>[0]['filters'];
  quickFilterIds?: readonly string[];
  children: ReactNode;
  searchField: string | string[];
  defaultFilters?: Array<{ id: string; value: unknown }>;
  resourceType: AdminLabelsV1LabelResourceType;
  disableQueryParams?: boolean;
} & (
  | { storageKey: string; persistence?: 'local' }
  | { storageKey?: never; persistence: 'session' }
);

export function FiltersProviderWithLabels({
  filters = [],
  quickFilterIds,
  storageKey,
  persistence,
  children,
  searchField,
  defaultFilters = [],
  resourceType,
  disableQueryParams,
}: FiltersProviderWithLabelsProps) {
  const { labelDefinitions } = useLabelsForResource({ resourceType });

  const labelFilter = useMemo(
    () =>
      labelDefinitions.length > 0
        ? createGroupedLabelFilter(labelDefinitions)
        : undefined,
    [labelDefinitions],
  );

  const allFilters = useMemo(
    () => (labelFilter ? [...filters, labelFilter] : filters),
    [filters, labelFilter],
  );

  return (
    <FiltersProvider
      filters={allFilters}
      quickFilterIds={quickFilterIds}
      {...(persistence === 'session'
        ? { persistence: 'session' as const }
        : { storageKey })}
      searchField={searchField}
      defaultFilters={defaultFilters}
      disableQueryParams={disableQueryParams}
    >
      <LabelFiltersContextProvider
        labelDefinitions={labelDefinitions}
        labelFilter={labelFilter}
      >
        {children}
      </LabelFiltersContextProvider>
    </FiltersProvider>
  );
}

function LabelFiltersContextProvider({
  labelDefinitions,
  labelFilter,
  children,
}: {
  labelDefinitions: LabelDefinition[];
  labelFilter: FilterDefinition | undefined;
  children: ReactNode;
}) {
  const { addFilter, removeFilter, activeFilters } = useFilters();

  const toggleLabelFilter = useCallback(
    (labelKey: string, value: string) => {
      if (!labelFilter) return;
      const composite = formatLabelValue(labelKey, value);
      const activeLabelValues =
        (activeFilters.find(f => f.id === LABEL_FILTER_ID)?.value as
          | string[]
          | undefined) ?? [];
      const nextLabelValues = activeLabelValues.includes(composite)
        ? activeLabelValues.filter(activeValue => activeValue !== composite)
        : [...activeLabelValues, composite];

      if (nextLabelValues.length === 0) {
        removeFilter(LABEL_FILTER_ID);
      } else {
        addFilter(labelFilter, nextLabelValues);
      }
    },
    [labelFilter, addFilter, removeFilter, activeFilters],
  );

  const contextValue = useMemo<LabelFiltersContextValue>(() => {
    const labelDefinitionsById = new Map(
      labelDefinitions.map(definition => [definition.id, definition]),
    );

    return {
      labelDefinitions,
      labelDefinitionsById,
      toggleLabelFilter,
    };
  }, [labelDefinitions, toggleLabelFilter]);

  return (
    <LabelFiltersContext.Provider value={contextValue}>
      {children}
    </LabelFiltersContext.Provider>
  );
}

export function useLabelFilters(
  labels: LabelEntry[] = EMPTY_LABELS,
): LabelFilters {
  const { labelDefinitions, labelDefinitionsById, toggleLabelFilter } =
    useContext(LabelFiltersContext);
  const definedLabels = useMemo(
    () =>
      labels.flatMap(label => {
        const definition = labelDefinitionsById.get(label.key);
        return definition && label.value
          ? [{ key: label.key, value: label.value, definition }]
          : [];
      }),
    [labelDefinitionsById, labels],
  );

  return {
    labelDefinitions,
    definedLabels,
    hasLabels: definedLabels.length > 0,
    toggleLabelFilter,
  };
}
